'use client';

import type { HlsStreamState } from '@/components/dashboard/HlsVideo';
import { LiveVideo } from '@/components/dashboard/LiveVideo';
import type { VideoLatencyEstimate } from '@/lib/video-latency';
import type { CopVideoStream } from './types';

/**
 * One camera's picture, and nothing else — no chrome, no metadata, no verbs.
 *
 * Lifted out of CopVideoWindow so every surface that draws a picture draws the
 * identical one. Its homes are the floating window over the map and the stage
 * (manual flight, and the split beside the map) — the details panel was a third
 * until video moved out of it. The operator must never be able to tell which
 * surface they are looking at from the picture itself: same HLS attach, same
 * .mp4 branch, same honest message for the protocols a browser cannot play,
 * same refusal below to invent a feed.
 *
 * Every branch fills its container, so the box never resizes under the operator
 * as the stream state changes.
 */
function isMp4Stream(stream: CopVideoStream): boolean {
  return stream.stream_url.endsWith('.mp4');
}

/**
 * Whether this stream actually paints a picture in a browser.
 *
 * Exported because a caller that overlays anything ON the picture — the manual
 * flight latency badge — must not overlay it on the RTMP/WebRTC branches, which
 * render an explanation rather than a video. One predicate, so the overlay and
 * the player can never disagree about whether there is a picture there.
 */
export function playsPicture(stream: CopVideoStream | null): boolean {
  if (!stream) return false;
  return stream.type === 'hls' || isMp4Stream(stream);
}

export function VideoStreamBody({
  callsign,
  stream,
  loading,
  error,
  onLatencyEstimate,
  onStreamState,
}: {
  callsign: string;
  stream: CopVideoStream | null;
  loading: boolean;
  error: string | null;
  /**
   * How far this picture is behind the live edge, for callers that must show it
   * (manual flight). Forwarded ONLY on the HLS branch, because it is the only
   * one where a browser can measure anything: a looping .mp4 has no live edge,
   * and the RTMP/WebRTC branches are not playing a picture at all. Those
   * callers therefore keep whatever they started with — null — and render
   * "not measured" rather than a figure nobody took.
   */
  onLatencyEstimate?: (estimate: VideoLatencyEstimate | null) => void;
  /**
   * Whether the HLS player is actually painting, for callers that overlay
   * something on the picture and must not overlay it on a dead one.
   *
   * Forwarded on the HLS branch only — the same branch `onLatencyEstimate`
   * takes, and the only one with a player that can fail. The `.mp4` branch is a
   * plain media element with no such state, so a caller that hears nothing has
   * a picture; it must default to that rather than to "not playing".
   */
  onStreamState?: (state: HlsStreamState) => void;
}) {
  if (loading && !stream) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
        Loading stream registry…
      </div>
    );
  }

  if (error && !stream) {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 text-center text-xs text-status-red-fg">
        {error}
      </div>
    );
  }

  // No stream registered for this aircraft.
  //
  // NEVER fall back to canned footage here. This picture is labelled with a live
  // aircraft's callsign, so stock video reads as that aircraft's camera. On
  // 2026-08-04 a real SkyPulse drone sitting on a desk showed an aerial of an
  // amusement park under "DRONE CAMERA - MAV-001", while its own two cameras
  // were pointed at grass. In front of a customer that is indistinguishable
  // from fabricating a feed. Say plainly that there is no video instead.
  if (!stream) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-4 text-center">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          No video feed
        </div>
        <div className="text-[10px] text-muted-foreground/70">
          no stream registered for this aircraft
        </div>
      </div>
    );
  }

  const isMp4 = isMp4Stream(stream);

  if (playsPicture(stream)) {
    return isMp4 ? (
      <video
        className="h-full w-full object-contain"
        src={stream.stream_url}
        aria-label={`Live video for ${callsign}`}
        autoPlay
        loop
        muted
        playsInline
      />
    ) : (
      <LiveVideo
        url={stream.stream_url}
        label={`Live video for ${callsign}`}
        controls={false}
        className="h-full w-full object-contain"
        onLatencyEstimate={onLatencyEstimate}
        onStreamState={onStreamState}
      />
    );
  }

  if (stream.type === 'rtmp') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-xs text-status-amber-fg">
        <span className="select-all break-all text-foreground">{stream.stream_url}</span>
        <span className="text-[11px] normal-case text-muted-foreground">
          Browsers cannot play RTMP directly. Use VLC, ffplay, or an RTMP→HLS gateway.
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-xs">
      <span className="text-status-blue-fg">WebRTC player</span>
      <span className="max-w-sm text-[11px] normal-case text-muted-foreground">
        Embedded WebRTC playback will connect here (signaling + ICE). For now, open your WebRTC
        viewer against the published session for this drone.
      </span>
    </div>
  );
}
